import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import { useTranslation } from "react-i18next";
import { IHolidayRow } from "../types";
import { Controller, useForm } from "react-hook-form";
import { useRecoilState } from "recoil";
import { holidaysState } from "../state";
import { FormAutocomplete, FormTextField } from "../../common/form-fields-wrappers";
import { DatePicker } from "@mui/x-date-pickers";
import TextField from "@mui/material/TextField";
import dayjs from "dayjs";
import { useContext, useEffect, useMemo, useState } from "react";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import DialogCloseButton from "../../../booking/components/DialogCloseButton";
import DialogTitle from "@mui/material/DialogTitle";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

function HolidayEditDialog(props: {
    open: boolean,
    title: string,
    rowIndex: number,
    isNew?: boolean,

    defaultTenant?: string,

    onClose: () => void,
    onSave?: (data: IHolidayRow) => Promise<boolean>,
}) {
    const { t } = useTranslation(['network', 'translation']);
    const [rowData, setRowData] = useRecoilState(holidaysState);

    const { globalData } = useContext(GlobalContext);
    const tenants = useMemo<string[]>(() => {
        return globalData?.user.legalEntities?.map((elem: any) => elem.name) || [];
    }, [globalData]);

    const [confirmOpen, setConfirmOpen] = useState(false);

    const validationSchema = yup.object({
        name: yup
            .string()
            .trim()
            .required("Required"),
        _date: yup
            .date()
            .typeError("Invalid Date")
            .required("Required"),
        tenant: yup
            .string()
            .required("Required"),
    });

    const form = useForm<IHolidayRow>({
        resolver: yupResolver(validationSchema, {}, { rawValues: true }),
    });

    useEffect(() => {
        if (!props.open) {
            return;
        }

        if (props.isNew) {
            form.reset({
                name: "",
                _date: new Date(),
                tenant: props.defaultTenant ?? "",
            });
        } else {
            form.reset(rowData[props.rowIndex]);
        }
    }, [props.open, props.isNew]);

    const handleDateChange =
        (onChange: any) =>
        (value: any) => {
            const asDate = dayjs(value).toDate();
            onChange(asDate);
        }

    async function handleSave(data: IHolidayRow) {
        setConfirmOpen(false);

        const dataCopy = { ...data };
        dataCopy.day = data._date.getDate();
        dataCopy.month = data._date.getMonth() + 1;
        dataCopy.year = data._date.getFullYear();

        if (!props.isNew) {
            const rowDataClone = [...rowData];
            rowDataClone[props.rowIndex] = dataCopy;
            setRowData(rowDataClone);
        }

        // If props.onSave is missing, just close
        if (!props.onSave) {
            props.onClose();
        }

        // If props.onSave is present, and returns true, then close
        if (props.onSave && await props.onSave(dataCopy)) {
            props.onClose()
        }
    }

    function handleSubmitForConfirmation() {
        setConfirmOpen(true);
    }

    function handleCloseConfirm() {
        setConfirmOpen(false);
    }

    return (
        <>
            <Dialog
                open={props.open}
                onClose={() => {}}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>{props.title}</DialogTitle>

                <DialogContent>
                    <Grid container columnSpacing={2}>
                        <Grid item xs={12} md={6}>
                            <FormTextField
                                name={"name"}
                                label={t("msHoliday.fields.holidayName")}
                                control={form.control}
                            />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <Controller
                                name={"_date"}
                                control={form.control}
                                render={({field: {onChange, value}}: any) => (
                                    <DatePicker
                                        label={t("msHoliday.fields.date")}
                                        onChange={handleDateChange(onChange)}
                                        value={value}
                                        renderInput={(props) => <TextField {...props} fullWidth />}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <FormAutocomplete
                                name={"tenant"}
                                label={t("msHoliday.fields.nation")}
                                control={form.control}
                                options={tenants}
                                getOptionLabel={(opt) => opt && t(`msHoliday.legalEntities.${opt}`)}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button onClick={props.onClose}>{t("msHoliday.labels.cancel")}</Button>
                    <Button onClick={handleSubmitForConfirmation}>{t("msHoliday.labels.save")}</Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={confirmOpen}
                onClose={handleCloseConfirm}
                maxWidth="sm"
            >
                <DialogCloseButton onClick={handleCloseConfirm} />

                <DialogTitle>{props.title}</DialogTitle>

                <DialogContent>{t("msHoliday.messages.doYouConfirm")}</DialogContent>

                <DialogActions>
                    <Button onClick={handleCloseConfirm}>{t("msHoliday.labels.cancel")}</Button>
                    <Button onClick={form.handleSubmit(handleSave)}>{t("msHoliday.labels.save")}</Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default HolidayEditDialog;
